import assertUnreachable from '../utils/assertUnreachable';
/**
 * Discriminator for every query source a project can execute queries against.
 * A query source is anything that can scan a schema and run a query returning
 * the standard table format (ResultColumns + rows behind a queryUuid): the
 * project warehouse, the semantic layer, the DuckDB compose engine, and —
 * later — CSV uploads, HTTP APIs, Google Sheets, etc.
 *
 * Deliberately NOT WarehouseTypes: sources are not warehouses, they plug in
 * above the warehouse client layer.
 */
export var QuerySourceType;
(function (QuerySourceType) {
    /** Metric queries compiled through the project's semantic layer. */
    QuerySourceType["SEMANTIC_LAYER"] = "semanticLayer";
    /** Raw SQL against the project's data warehouse (SQL runner). */
    QuerySourceType["SQL"] = "sql";
    /**
     * DuckDB SQL over previous results (compose engine). References expose
     * other queries' results as named tables, so this is the merge/transform
     * step of a multi-source pipeline.
     */
    QuerySourceType["DUCKDB"] = "duckdb";
    /** Durable external tables queried with DuckDB; enterprise only. */
    QuerySourceType["EXTERNAL"] = "external";
    /**
     * Python `run(inputs, params)` over previous results, executed in a
     * sandbox. References expose other queries' results as pandas DataFrames.
     */
    QuerySourceType["PYTHON"] = "python";
    /** A query sent live to an external API, such as HogQL to PostHog. */
    QuerySourceType["API"] = "api";
})(QuerySourceType || (QuerySourceType = {}));
/** The external APIs an api query source can call live. */
export var ApiConnectorType;
(function (ApiConnectorType) {
    ApiConnectorType["POSTHOG"] = "posthog";
    ApiConnectorType["SEARCH_CONSOLE"] = "search_console";
    ApiConnectorType["GA4"] = "ga4";
})(ApiConnectorType || (ApiConnectorType = {}));
export const DEFAULT_SOURCE_QUERY_LIMIT = 500;
export const getSourceQueryLimit = (query) => query.limit ?? DEFAULT_SOURCE_QUERY_LIMIT;
/** A node that returned at least its limit of rows reached it, so more rows may exist. */
export const hasReachedSourceQueryLimit = (query, rowCount) => rowCount >= getSourceQueryLimit(query);
/** The metric query a semanticLayer node runs; unset fields take the empty-query defaults. */
export const metricQueryOfSemanticNode = (query) => ({
    exploreName: query.exploreName,
    dimensions: query.dimensions,
    metrics: query.metrics,
    filters: query.filters ?? {},
    sorts: query.sorts ?? [],
    limit: query.limit ?? DEFAULT_SOURCE_QUERY_LIMIT,
    tableCalculations: query.tableCalculations ?? [],
    additionalMetrics: query.additionalMetrics,
    customDimensions: query.customDimensions,
    metricOverrides: query.metricOverrides,
    dimensionOverrides: query.dimensionOverrides,
    timezone: query.timezone,
});
/**
 * The results a node reads, as {tableOrInputName: nodeIdOrQueryUuid}. The
 * array shorthand names each result by its own node id. Empty for nodes that
 * read no other results.
 */
export const getSourceQueryResultReferences = (query) => {
    switch (query.sourceType) {
        case QuerySourceType.DUCKDB:
        case QuerySourceType.PYTHON:
            if (query.references === undefined)
                return {};
            return Array.isArray(query.references)
                ? Object.fromEntries(query.references.map((nodeId) => [nodeId, nodeId]))
                : query.references;
        case QuerySourceType.SEMANTIC_LAYER:
        case QuerySourceType.SQL:
        case QuerySourceType.EXTERNAL:
        case QuerySourceType.API:
            return {};
        default:
            return assertUnreachable(query, 'Unknown source type');
    }
};
//# sourceMappingURL=querySources.js.map